/**
 * What Badger can put on screen (docs/features/badger-cohost.md, Sandbox): he holds no credential that can write, so he
 * asks the console of the live host he hears to run one of the stage's actions (`stageActions.ts`) over LiveKit RPC. A
 * live host's page answers four methods while that host is live on the stage, only to the stage's Badger, and each runs
 * the very function the console's buttons run, as that host. The answer is what was shown, or why not in the console's
 * words, so Badger can say it; the host sees the element on their canvas and, through `onDone`, the console's line.
 */

import { useEffect, useRef } from 'react'
import { RpcError, type Room, type RpcInvocationData } from 'livekit-client'
import { GRAPHIC_SLOTS, type GraphicSlot, type RaceGender } from './graphics'
import type { StageActionResult, StageActions } from './stageActions'
import { COHOST_ATTRIBUTE, COHOST_PERSONA, isCohostOf } from './useCohost'

/** `{"athlete_id", "hold_s"?}`: the athlete's card in the lower third. */
export const COHOST_SHOW_ATHLETE_METHOD = 'cohost.show_athlete'
/** `{"route_id", "gender"?, "athlete_ids"?, "limit"?, "title"?}`: a leaderboard in the panel. */
export const COHOST_SHOW_STANDINGS_METHOD = 'cohost.show_standings'
/** `{"athlete_id"}`: the host's map, flying to the athlete. */
export const COHOST_SHOW_ON_MAP_METHOD = 'cohost.show_on_map'
/** `{"slot"?}`: clears one slot, or both without one. */
export const COHOST_CLEAR_METHOD = 'cohost.clear'

export const COHOST_METHODS = [COHOST_SHOW_ATHLETE_METHOD, COHOST_SHOW_STANDINGS_METHOD, COHOST_SHOW_ON_MAP_METHOD, COHOST_CLEAR_METHOD] as const
export type CohostMethod = (typeof COHOST_METHODS)[number]

/** The RPC errors these answer with, numbered as HTTP's (LiveKit keeps 1001 to 1999 for its own). */
export const COHOST_RPC_ERRORS = { notBadger: 403, refused: 409, invalid: 422, failed: 500 } as const

export const NOT_BADGER = 'Only this stage’s Badger can do that'
export const UNEXPECTED = 'That didn’t go through'

/** What one of Badger's calls did: the method and the action's answer (shown, or refused and why). */
export interface CohostDone {
  method: CohostMethod
  result: StageActionResult
}

/** The caller is the stage's Badger: an agent in the room (only our worker can join as one) for this stage, as Badger. */
export function isBadgerCaller(room: Pick<Room, 'remoteParticipants'>, callerIdentity: string, stageId: string): boolean {
  const caller = room.remoteParticipants.get(callerIdentity)
  return caller !== undefined && isCohostOf(caller, stageId) && caller.attributes?.[COHOST_ATTRIBUTE] === COHOST_PERSONA
}

type Request = Record<string, unknown>

const invalid = (message: string) => new RpcError(COHOST_RPC_ERRORS.invalid, message)

/** The payload as a JSON object, else a 422. */
export function requestOf(payload: string): Request {
  let data: unknown
  try {
    data = JSON.parse(payload)
  } catch {
    throw invalid('The payload is not JSON')
  }
  if (typeof data !== 'object' || data === null || Array.isArray(data)) throw invalid('The payload is not a JSON object')
  return data as Request
}

/** A required id, a non-empty string, else a 422 naming it. */
function idOf(request: Request, key: string): string {
  const value = request[key]
  if (typeof value !== 'string' || !value.trim()) throw invalid(`${key} must be a non-empty string`)
  return value
}

/** An optional field: missing or null is none; anything `accepts` refuses is a 422 saying what it must be. */
function optional<T>(request: Request, key: string, expected: string, accepts: (value: unknown) => value is T): T | null {
  const value = request[key]
  if (value === undefined || value === null) return null
  if (!accepts(value)) throw invalid(`${key} must be ${expected}`)
  return value
}

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)
const isPositive = (value: unknown): value is number => isNumber(value) && value > 0
const isText = (value: unknown): value is string => typeof value === 'string'
const isIds = (value: unknown): value is string[] => Array.isArray(value) && value.every((id) => typeof id === 'string' && id.trim() !== '')
const isGender = (value: unknown): value is RaceGender => value === 'female' || value === 'male'
const isSlot = (value: unknown): value is GraphicSlot => (GRAPHIC_SLOTS as readonly unknown[]).includes(value)

type Run = (actions: StageActions) => Promise<StageActionResult>

/** Each method's payload, read before anything runs (a 422 on a bad one), as the stage action it runs. */
const READS: Record<CohostMethod, (request: Request) => Run> = {
  [COHOST_SHOW_ATHLETE_METHOD]: (request) => {
    const athleteId = idOf(request, 'athlete_id')
    const holdS = optional(request, 'hold_s', 'a positive number', isPositive) ?? undefined
    return (actions) => actions.showAthlete(athleteId, { holdS })
  },
  [COHOST_SHOW_STANDINGS_METHOD]: (request) => {
    const input = {
      routeId: idOf(request, 'route_id'),
      gender: optional(request, 'gender', 'female, male or null', isGender),
      athleteIds: optional(request, 'athlete_ids', 'a list of ids or null', isIds),
      limit: optional(request, 'limit', 'a number or null', isNumber),
      title: optional(request, 'title', 'a string or null', isText),
    }
    return (actions) => actions.showStandings(input)
  },
  [COHOST_SHOW_ON_MAP_METHOD]: (request) => {
    const athleteId = idOf(request, 'athlete_id')
    return (actions) => actions.showOnMap(athleteId)
  },
  [COHOST_CLEAR_METHOD]: (request) => {
    const slot = optional(request, 'slot', 'lower_third, panel or null', isSlot) ?? undefined
    return (actions) => actions.clearGraphic(slot)
  },
}

export interface CohostHandlerContext {
  room: Pick<Room, 'remoteParticipants'>
  stageId: string
  actions: StageActions
  /** Told what each call that ran did, shown or refused. */
  onDone?(done: CohostDone): void
}

/**
 * Answers one of Badger's calls: `{"shown": …}` once the action showed it; a 403 for anyone but the stage's Badger, a
 * 422 for a payload that isn't the method's, a 409 with the action's reason when it refused, and a 500 for anything else.
 */
export async function answerCohost(
  context: CohostHandlerContext,
  method: CohostMethod,
  { callerIdentity, payload }: Pick<RpcInvocationData, 'callerIdentity' | 'payload'>,
): Promise<string> {
  if (!isBadgerCaller(context.room, callerIdentity, context.stageId)) throw new RpcError(COHOST_RPC_ERRORS.notBadger, NOT_BADGER)
  const run = READS[method](requestOf(payload))
  let result: StageActionResult
  try {
    result = await run(context.actions)
    context.onDone?.({ method, result })
  } catch {
    throw new RpcError(COHOST_RPC_ERRORS.failed, UNEXPECTED)
  }
  if (!result.ok) throw new RpcError(COHOST_RPC_ERRORS.refused, result.reason)
  return JSON.stringify({ shown: result.shown })
}

export interface CohostHandlersInput {
  room: Pick<Room, 'remoteParticipants' | 'registerRpcMethod' | 'unregisterRpcMethod'>
  stageId: string
  /** This tab's host is live on the stage: only then does the page answer Badger. */
  live: boolean
  /** The stage's actions (`useStageActions`), or null to answer nothing. */
  actions: StageActions | null
  /** Told what each of Badger's calls did (the console's line), with the newest callback. */
  onDone?(done: CohostDone): void
}

/**
 * Answers Badger's calls on the room while this tab's host is live on the stage, and stops when they aren't, when the
 * stage changes or the page goes. The room holds one handler per method (registering one twice throws), and only this
 * page registers these.
 */
export function useCohostHandlers({ room, stageId, live, actions, onDone }: CohostHandlersInput): void {
  const told = useRef(onDone)
  told.current = onDone
  useEffect(() => {
    if (!live || !actions) return
    const context: CohostHandlerContext = { room, stageId, actions, onDone: (done) => told.current?.(done) }
    for (const method of COHOST_METHODS) room.registerRpcMethod(method, (data) => answerCohost(context, method, data))
    return () => {
      for (const method of COHOST_METHODS) room.unregisterRpcMethod(method)
    }
  }, [room, stageId, live, actions])
}
